import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

/**
 * Animates a numeric value found inside a string (preserves prefix/suffix).
 * Examples:
 *   value="$12,700M" -> animates 0 -> 12700, displayed as "$12,700M"
 *   value="28.1%"    -> animates 0 -> 28.1, displayed as "28.1%"
 *   value="47"       -> animates 0 -> 47
 */
export default function AnimatedCounter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [current, setCurrent] = useState(0);

  const match = String(value).match(/[\d,.]+/);
  const numericStr = match ? match[0].replace(/,/g, "") : "0";
  const target = parseFloat(numericStr);
  const before = match ? String(value).slice(0, match.index) : "";
  const after = match ? String(value).slice(match.index + match[0].length) : "";
  const decimals = numericStr.includes(".") ? 1 : 0;

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setCurrent(target * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  const formatted =
    decimals === 0
      ? Math.round(current).toLocaleString("en-US")
      : current.toFixed(decimals);

  return (
    <span ref={ref} className="tabular-nums">
      {before}{formatted}{after}
    </span>
  );
}
